import { api } from "@/lib/orgApi";
import { ENCOUNTER_CLASSES } from "@/lib/encounterApi";

export const VISIT_TYPE_OPTIONS = [
  { code: "185349003", display: "New Visit" },
  { code: "390906007", display: "Follow-up Visit" },
  { code: "448337001", display: "Tele Visit" },
];

export const APPOINTMENT_STATUSES = [
  { code: "booked", display: "Scheduled" },
  { code: "arrived", display: "Arrived" },
  { code: "cancelled", display: "Cancelled" },
  { code: "noshow", display: "No-Show" },
];

export const CONFIRMATION_STATUSES = [
  { code: "confirmed", display: "Confirmed" },
  { code: "pending", display: "Pending" },
  { code: "unconfirmed", display: "Unconfirmed" },
];

export const DURATION_OPTIONS = [
  { code: "15", display: "15 minutes" },
  { code: "30", display: "30 minutes" },
  { code: "45", display: "45 minutes" },
  { code: "60", display: "60 minutes" },
];

export const REMINDER_METHODS = [
  { code: "sms", display: "Text Message" },
  { code: "phone", display: "Phone Call" },
  { code: "email", display: "Email" },
  { code: "none", display: "No reminder" },
];

export const REMINDER_OFFSETS = [
  { code: "24h", display: "24 hours before" },
  { code: "2h", display: "2 hours before" },
  { code: "1h", display: "1 hour before" },
];

export const REPEAT_OPTIONS = [{ code: "does-not-repeat", display: "Does not repeat" }];

export const PLANNED_CLASSES = ENCOUNTER_CLASSES;

export const NO_SHOW_RATE_UNAVAILABLE = "Not available";

export function visitTypeLabel(code) {
  return VISIT_TYPE_OPTIONS.find((o) => o.code === code)?.display || "";
}

export function appointmentStatusLabel(code) {
  if (code === "pending") return "Scheduled";
  return APPOINTMENT_STATUSES.find((o) => o.code === code)?.display || code || "";
}

/** Leftover FHIR Appointment.status=pending is displayed as Scheduled, not as a UI status. */
export function appointmentStatusTone(code) {
  return code === "pending" ? "booked" : code || "";
}

export function confirmationStatusLabel(code) {
  if (!code) return "—";
  return CONFIRMATION_STATUSES.find((o) => o.code === code)?.display || code;
}

export function appointmentDateKey(iso) {
  const match = String(iso || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

export function formatAppointmentTime(iso) {
  const match = String(iso || "").match(/T(\d{2}):(\d{2})/);
  if (!match) return "—";
  const hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
}

export function actorDisplay(appointment, prefix) {
  const match = (appointment?.participant || []).find((p) =>
    String(p.actor?.reference || "").startsWith(prefix),
  );
  return match?.actor?.display || "";
}

export function providerDisplay(appointment) {
  const match = (appointment?.participant || []).find((p) =>
    (p.type || []).some((t) => (t.coding || []).some((c) => c.code === "ATND")),
  );
  return match?.actor?.display || "";
}

export function referringProviderDisplay(appointment) {
  const match = (appointment?.participant || []).find((p) =>
    (p.type || []).some((t) => (t.coding || []).some((c) => c.code === "REF")),
  );
  return match?.actor?.display || "";
}

export function appointmentVisitType(appointment) {
  return (
    appointment?.appointmentType?.text ||
    visitTypeLabel(appointment?.appointmentType?.coding?.[0]?.code) ||
    ""
  );
}

export function appointmentNotes(appointment) {
  const comment = (appointment?.comment || "").trim();
  if (comment) return comment;
  return (appointment?.reasonCode || [])
    .map((r) => r.text)
    .filter(Boolean)
    .join("; ");
}

export function sexLetter(gender) {
  const value = String(gender || "").toLowerCase();
  if (value === "male") return "M";
  if (value === "female") return "F";
  if (value === "other") return "O";
  return "";
}

export function patientCalendarLabel({ name, age, sex } = {}) {
  const displayName = name || "—";
  if (age != null && age !== "" && sex) return `${displayName} (${age}/${sex})`;
  if (age != null && age !== "") return `${displayName} (${age})`;
  if (sex) return `${displayName} (${sex})`;
  return displayName;
}

export function filterAppointmentsForDay(items, dateKey, locationId) {
  return (items || [])
    .filter((row) => appointmentDateKey(row.start) === dateKey)
    .filter((row) => !locationId || locationId === "all" || row.locationId === locationId)
    .sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
}

export function listAppointments(params = {}) {
  return api.get("/appointments", { params }).then((r) => r.data);
}

export function createAppointment(payload) {
  return api.post("/appointments", payload).then((r) => r.data);
}

export function getAppointment(appointmentId) {
  return api.get(`/appointments/${encodeURIComponent(appointmentId)}`).then((r) => r.data);
}

export function updateAppointment(appointmentId, payload) {
  return api.put(`/appointments/${encodeURIComponent(appointmentId)}`, payload).then((r) => r.data);
}

export function startEncounterFromAppointment(appointmentId, options = {}) {
  const params = options.patientId ? { patient: options.patientId } : undefined;
  return api
    .post(`/appointments/${encodeURIComponent(appointmentId)}/start-encounter`, null, { params })
    .then((r) => r.data);
}

export const DEFAULT_APPOINTMENT_SMS_MESSAGE =
  "EMRbyMD appointment SMS test. This is a test message.";

export function getAppointmentSmsPreview(appointmentId) {
  return api.get(`/appointments/${encodeURIComponent(appointmentId)}/sms-test`).then((r) => r.data);
}

export function sendAppointmentSmsTest(appointmentId, { message } = {}) {
  return api
    .post(`/appointments/${encodeURIComponent(appointmentId)}/sms-test`, {
      message: message || DEFAULT_APPOINTMENT_SMS_MESSAGE,
    })
    .then((r) => r.data);
}

/** Start Encounter is shown only for Arrived appointments without a linked Encounter. */
export function canStartEncounter(appointment) {
  if (!appointment || appointment.encounterId) return false;
  if (appointment.status !== "arrived") return false;
  if (appointment.canStartEncounter === false) return false;
  return true;
}

export function listSchedulingProviders() {
  return api.get("/appointments/providers").then((r) => r.data.items || []);
}

export function searchPatients(patients, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return patients.slice(0, 8);
  return patients.filter((p) => {
    const name = `${(p.name?.[0]?.given || []).join(" ")} ${p.name?.[0]?.family || ""}`.toLowerCase();
    const mrn = (p.identifier || [])
      .map((i) => i.value || "")
      .join(" ")
      .toLowerCase();
    const dob = (p.birthDate || "").toLowerCase();
    const phone = (p.telecom || [])
      .filter((t) => t.system === "phone")
      .map((t) => t.value || "")
      .join(" ")
      .toLowerCase();
    return name.includes(q) || mrn.includes(q) || dob.includes(q) || phone.includes(q);
  }).slice(0, 8);
}

export function addMinutesToTime(timeValue, minutes) {
  if (!timeValue) return "";
  const [h, m] = timeValue.split(":").map((n) => Number(n));
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const total = h * 60 + m + Number(minutes || 0);
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hh = String(Math.floor(wrapped / 60)).padStart(2, "0");
  const mm = String(wrapped % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function resolveAppointmentLocationId({ queryLocation, contextLocation, locations } = {}) {
  const ids = new Set((locations || []).map((l) => l.id));
  if (queryLocation && ids.has(queryLocation)) return queryLocation;
  if (contextLocation && ids.has(contextLocation)) return contextLocation;
  return "";
}

export function isUpcomingAppointment(appointment, todayKey) {
  return appointmentDateKey(appointment?.start) >= String(todayKey || "");
}

export function filterAppointmentsByMonth(items, monthKey) {
  if (!monthKey) return items || [];
  return (items || []).filter((row) => appointmentDateKey(row.start).startsWith(monthKey));
}

export function groupPatientAppointments(items, todayKey) {
  const upcoming = [];
  const past = [];
  for (const row of items || []) {
    if (isUpcomingAppointment(row, todayKey)) upcoming.push(row);
    else past.push(row);
  }
  upcoming.sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
  past.sort((a, b) => String(b.start || "").localeCompare(String(a.start || "")));
  return { upcoming, past };
}

export function chartReturnPath(patientId) {
  if (!patientId) return "";
  return `/patients/${encodeURIComponent(patientId)}?tab=appointments`;
}

export function safeChartReturnPath(value) {
  const raw = String(value || "");
  if (!raw.startsWith("/patients/")) return "";
  if (raw.startsWith("//") || raw.includes("://")) return "";
  return raw;
}

export function safeAppointmentReturnPath(value) {
  const chart = safeChartReturnPath(value);
  if (chart) return chart;
  const raw = String(value || "");
  if (raw === "/appointments") return raw;
  if (!raw.startsWith("/appointments?")) return "";
  if (raw.includes("://") || raw.startsWith("//")) return "";
  return raw;
}

export function newAppointmentPath(locationIdOrOptions) {
  const options =
    typeof locationIdOrOptions === "object" && locationIdOrOptions
      ? locationIdOrOptions
      : { locationId: locationIdOrOptions };
  const params = new URLSearchParams();
  if (options.locationId && options.locationId !== "all") {
    params.set("location", options.locationId);
  }
  if (options.patientId) params.set("patient", options.patientId);
  if (options.returnTo) params.set("return", options.returnTo);
  const query = params.toString();
  return query ? `/appointments/new?${query}` : "/appointments/new";
}

export function editAppointmentPath(appointmentId, options = {}) {
  if (!appointmentId) return "/appointments";
  const params = new URLSearchParams();
  const returnTo =
    options.returnTo ||
    appointmentsCalendarPath({ locationId: options.locationId, date: options.date });
  if (returnTo) params.set("return", returnTo);
  const query = params.toString();
  return `/appointments/${encodeURIComponent(appointmentId)}/edit${query ? `?${query}` : ""}`;
}

export function appointmentsCalendarPath(locationIdOrOptions) {
  const options =
    typeof locationIdOrOptions === "object" && locationIdOrOptions
      ? locationIdOrOptions
      : { locationId: locationIdOrOptions };
  const params = new URLSearchParams();
  if (options.locationId && options.locationId !== "all") {
    params.set("location", options.locationId);
  }
  if (options.date) params.set("date", options.date);
  const query = params.toString();
  return query ? `/appointments?${query}` : "/appointments";
}

const WRITABLE_APPOINTMENT_STATUS = new Set(["booked", "arrived", "cancelled", "noshow"]);
const CONFIRMATION_CODES = new Set(["confirmed", "pending", "unconfirmed"]);

export function formFromAppointment(appointment) {
  const start = String(appointment?.start || "");
  const timeMatch = start.match(/T(\d{2}:\d{2})/);
  const storedStatus = appointment?.status === "pending" ? "booked" : appointment?.status || "booked";
  const confirmation = appointment?.confirmationStatus || "confirmed";
  const duration = appointment?.minutesDuration != null ? String(appointment.minutesDuration) : "30";
  return {
    patientId: appointment?.patientId || "",
    locationId: appointment?.locationId || "",
    providerUserId: appointment?.providerUserId || "",
    referringPractitionerId: appointment?.referringPractitionerId || "",
    visitType: appointment?.appointmentType?.coding?.[0]?.code || "",
    plannedClass: appointment?.plannedClass?.code || "AMB",
    reason: appointment?.reasonCode?.[0]?.text || "",
    comment: appointment?.comment || "",
    patientInstruction: appointment?.patientInstruction || "",
    date: appointmentDateKey(start) || "",
    startTime: timeMatch ? timeMatch[1] : "10:00",
    duration,
    status: WRITABLE_APPOINTMENT_STATUS.has(storedStatus) ? storedStatus : "booked",
    confirmationStatus: CONFIRMATION_CODES.has(confirmation) ? confirmation : "confirmed",
    reminderMethod: appointment?.reminderMethod || "sms",
    reminderOffset: appointment?.reminderOffset || "24h",
    recurrence: appointment?.recurrence || "does-not-repeat",
    resourceName: appointment?.resourceName || "",
  };
}

export function toAppointmentPayload(form) {
  const start = form.date && form.startTime ? `${form.date}T${form.startTime}:00` : "";
  return {
    patientId: form.patientId,
    locationId: form.locationId || undefined,
    providerUserId: form.providerUserId || undefined,
    referringPractitionerId: form.referringPractitionerId || undefined,
    visitType: form.visitType || undefined,
    plannedClass: form.plannedClass || "AMB",
    reason: form.reason || undefined,
    comment: form.comment || undefined,
    patientInstruction: form.patientInstruction || undefined,
    start,
    minutesDuration: Number(form.duration) || 30,
    status: form.status || "booked",
    confirmationStatus: form.confirmationStatus || "confirmed",
    reminderMethod: form.reminderMethod || undefined,
    reminderOffset: form.reminderOffset || undefined,
    recurrence: form.recurrence || "does-not-repeat",
    resourceName: form.resourceName || undefined,
  };
}
